import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Events,
  TreatmentPlanItem,
  TreatmentPlansRepositoryService,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientTreatmentPlanItemsStore — the working pool of pending action items.
 *
 * Items are added/removed independently and later snapshotted into a saved
 * TreatmentPlan via PatientTreatmentPlansStore.createFromItems(). The pool
 * persists across patient sessions; it is not consumed by being snapshotted.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientTreatmentPlanItemsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(TreatmentPlansRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<TreatmentPlanItem[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.TreatmentPlanTreatmentPlanActionItemChanged;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  add(item: TreatmentPlanItem): Observable<number> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientTreatmentPlanItemsStore.add() called with no current patient');
    }
    const previous = this._items();
    this._items.set([...previous, item]);
    return this.repo.addTreatmentPlanItemToPatient(patient.id, item).pipe(
      tap({
        next: newId => {
          if (typeof newId === 'number') {
            item.id = newId;
            this._items.set([...this._items()]);
          }
          this.reload();
        },
        error: error => {
          this._items.set(previous);
          
        }
      })
    );
  }

  remove(itemId: number): Observable<unknown> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientTreatmentPlanItemsStore.remove() called with no current patient');
    }
    const previous = this._items();
    this._items.set(previous.filter(i => i.id !== itemId));
    return this.repo.removeTreatmentPlanItemFromPatient(patient.id, itemId).pipe(
      tap({
        error: error => {
          this._items.set(previous);
          
        }
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientTreatmentPlanItems(patientId).subscribe({
      next: items => {
        const sorted = [...items].sort((a, b) =>
          a.createdDate > b.createdDate ? 1 : -1
        );
        this._items.set(sorted);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient treatment plan items');
        
      }
    });
  }
}
